import { useState, useRef, useEffect, useCallback } from "react";
import { RotateCw, Gift, Target, Sparkles } from "lucide-react";

/* ==============================
   MINI WHEEL OF FORTUNE
   ============================== */
const MiniWheel = () => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);

  const PRIZES = [
    { label: "10%", color: "#C65D47" },
    { label: "PERDU", color: "#1D3557" },
    { label: "5€", color: "#8C9D78" },
    { label: "PERDU", color: "#2A4A6D" },
    { label: "20%", color: "#C65D47" },
    { label: "PERDU", color: "#1D3557" },
    { label: "CADEAU", color: "#8C9D78" },
    { label: "PERDU", color: "#2A4A6D" },
  ];

  const segAngle = 360 / PRIZES.length;
  const size = 200;
  const cx = size / 2;
  const r = cx - 4;

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);
    // Pick a random prize index first, then calculate the rotation to land on it
    const winIdx = Math.floor(Math.random() * PRIZES.length);
    const extra = 360 * (5 + Math.floor(Math.random() * 3));
    // The pointer is at the top. Segment i center is at (i + 0.5) * segAngle from top.
    // We need to rotate so that segment winIdx is under the pointer.
    const targetAngle = winIdx * segAngle + segAngle / 2;
    const newRot = rotation + extra + (360 - (targetAngle % 360));
    setRotation(newRot);
    setTimeout(() => {
      setResult(PRIZES[winIdx]);
      setSpinning(false);
    }, 4000);
  };

  const segPath = (i) => {
    const a1 = (i * segAngle - 90) * (Math.PI / 180);
    const a2 = ((i + 1) * segAngle - 90) * (Math.PI / 180);
    return `M${cx},${cx} L${cx + r * Math.cos(a1)},${cx + r * Math.sin(a1)} A${r},${r} 0 0,1 ${cx + r * Math.cos(a2)},${cx + r * Math.sin(a2)} Z`;
  };

  const textPos = (i) => {
    const a = ((i + 0.5) * segAngle - 90) * (Math.PI / 180);
    const tr = r * 0.65;
    return { x: cx + tr * Math.cos(a), y: cx + tr * Math.sin(a), rot: (i + 0.5) * segAngle };
  };

  return (
    <div className="flex flex-col items-center pt-2" data-testid="mini-wheel">
      <div className="relative mb-3">
        {/* Pointer */}
        <div className="absolute top-[-6px] left-1/2 -translate-x-1/2 z-10">
          <svg width="20" height="24" viewBox="0 0 20 24"><polygon points="10,24 0,0 20,0" fill="#C65D47" /></svg>
        </div>
        {/* Glow */}
        <div className="absolute inset-[-8px] rounded-full" style={{ background: "conic-gradient(from 0deg, #C65D47, #8C9D78, #1D3557, #C65D47)", opacity: spinning ? 0.6 : 0.3, filter: "blur(6px)", transition: "opacity 0.5s" }} />
        <svg width={size} height={size} className="relative z-[2] drop-shadow-xl" style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.05, 0.99)" : "none" }}>
          <circle cx={cx} cy={cx} r={r + 2} fill="#1D3557" />
          {PRIZES.map((p, i) => <path key={i} d={segPath(i)} fill={p.color} stroke="#FDFBF7" strokeWidth="1" />)}
          {PRIZES.map((_, i) => { const a = (i * segAngle - 90) * Math.PI / 180; return <circle key={`d${i}`} cx={cx + (r - 6) * Math.cos(a)} cy={cx + (r - 6) * Math.sin(a)} r="2" fill="#FFD700" opacity="0.6" />; })}
          {PRIZES.map((p, i) => { const t = textPos(i); return <text key={`t${i}`} x={t.x} y={t.y} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="10" fontWeight="bold" transform={`rotate(${t.rot}, ${t.x}, ${t.y})`}>{p.label}</text>; })}
          <circle cx={cx} cy={cx} r="20" fill="#FDFBF7" stroke="#1D3557" strokeWidth="2" />
          <circle cx={cx} cy={cx} r="14" fill="#1D3557" />
          <text x={cx} y={cx + 1} textAnchor="middle" dominantBaseline="middle" fill="#FFD700" fontSize="11" fontWeight="bold">GO</text>
        </svg>
      </div>

      {result && (
        <div className={`text-center px-4 py-2 rounded-xl mb-2 text-xs font-bold ${result.label === "PERDU" ? "bg-stone-100 text-stone-500" : "bg-[#8C9D78]/20 text-[#1D3557]"}`}>
          {result.label === "PERDU" ? "Pas de chance !" : `Bravo ! ${result.label}`}
        </div>
      )}

      <button onClick={spin} disabled={spinning} className="flex items-center gap-1.5 px-5 py-2 rounded-full text-white text-xs font-semibold disabled:opacity-60 transition-all" style={{ backgroundColor: "#C65D47" }}>
        {spinning ? <><RotateCw className="w-3 h-3 animate-spin" /> En cours...</> : <><Sparkles className="w-3 h-3" /> Tourner la roue</>}
      </button>
    </div>
  );
};

/* ==============================
   MINI SCRATCH CARD
   ============================== */
const MiniScratch = () => {
  const canvasRef = useRef(null);
  const [scratching, setScratch] = useState(false);
  const [pct, setPct] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [init, setInit] = useState(false);
  const [prize] = useState(() => ["10€ OFFERTS", "20% DE REMISE", "LIVRAISON OFFERTE", "CADEAU SURPRISE"][Math.floor(Math.random() * 4)]);

  const W = 230, H = 140;

  const initCanvas = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#8C9D78"); g.addColorStop(1, "#6B7C5A");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.06)"; ctx.lineWidth = 1;
    for (let i = -H; i < W + H; i += 10) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + H, H); ctx.stroke(); }
    for (let i = 0; i < 40; i++) { ctx.beginPath(); ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 1.5 + 0.5, 0, Math.PI * 2); ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.25})`; ctx.fill(); }
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 14px system-ui"; ctx.textAlign = "center";
    ctx.fillText("GRATTEZ POUR GAGNER", W / 2, H / 2 - 5);
    ctx.font = "10px system-ui"; ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText("Utilisez votre doigt", W / 2, H / 2 + 14);
    ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 1.5; ctx.strokeRect(6, 6, W - 12, H - 12);
    setInit(true);
  }, []);

  useEffect(() => { const t = setTimeout(initCanvas, 100); return () => clearTimeout(t); }, [initCanvas]);

  const scratch = useCallback((e) => {
    const c = canvasRef.current; if (!c || revealed) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    const sx = W / rect.width, sy = H / rect.height;
    const cx = e.clientX || e.touches?.[0]?.clientX, cy = e.clientY || e.touches?.[0]?.clientY;
    if (!cx || !cy) return;
    const x = (cx - rect.left) * sx, y = (cy - rect.top) * sy;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath(); ctx.arc(x, y, 18, 0, Math.PI * 2); ctx.fill();
    const d = ctx.getImageData(0, 0, W, H); let t = 0; const total = d.data.length / 4;
    for (let i = 3; i < d.data.length; i += 4) { if (d.data[i] < 128) t++; }
    const p = Math.round((t / total) * 100); setPct(p);
    if (p > 50) setRevealed(true);
  }, [revealed]);

  const reset = () => { setRevealed(false); setPct(0); setInit(false); setTimeout(initCanvas, 50); };

  return (
    <div className="flex flex-col items-center pt-2" data-testid="mini-scratch">
      {/* Progress */}
      <div className="w-[230px] mb-2">
        <div className="h-1 bg-stone-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#8C9D78] to-[#C65D47] rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="relative mb-3">
        <div className="absolute inset-1 bg-black/8 rounded-xl blur-sm translate-y-1" />
        <div className="relative rounded-xl overflow-hidden" style={{ width: W, height: H }}>
          <div className="absolute inset-0 bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] flex flex-col items-center justify-center">
            <Gift className="w-7 h-7 text-[#FFD700] mb-1.5" />
            <p className="text-white font-bold text-base">{prize}</p>
            <p className="text-white/50 text-[10px] mt-0.5">Félicitations !</p>
          </div>
          {!revealed && (
            <canvas ref={canvasRef} width={W} height={H}
              className={`absolute inset-0 rounded-xl ${init ? "cursor-pointer" : ""}`}
              style={{ touchAction: "none" }}
              onMouseDown={() => init && setScratch(true)} onMouseUp={() => setScratch(false)}
              onMouseLeave={() => setScratch(false)} onMouseMove={(e) => init && scratching && scratch(e)}
              onTouchStart={() => init && setScratch(true)} onTouchEnd={() => setScratch(false)}
              onTouchMove={(e) => { if (!init) return; e.preventDefault(); scratch(e); }}
            />
          )}
        </div>
      </div>

      {revealed && <p className="text-[10px] font-bold text-[#8C9D78] mb-2">Vous avez gagné !</p>}
      <button onClick={reset} className="text-[10px] px-4 py-1.5 rounded-full border border-[#8C9D78]/30 text-[#1D3557] font-medium hover:bg-[#8C9D78]/10">Nouvelle carte</button>
    </div>
  );
};

/* ==============================
   MINI INSTANT WIN
   ============================== */
const MiniInstant = () => {
  const PRIZES = [
    { text: "10€", win: true }, { text: "PERDU", win: false }, { text: "CADEAU", win: true },
    { text: "PERDU", win: false }, { text: "20%", win: true }, { text: "PERDU", win: false },
  ];
  const [cards, setCards] = useState(() => [...PRIZES].sort(() => Math.random() - 0.5));
  const [flipped, setFlipped] = useState([]);
  const [tries, setTries] = useState(2);
  const [done, setDone] = useState(false);
  const [won, setWon] = useState(null);

  const flip = (i) => {
    if (done || flipped.includes(i) || tries <= 0) return;
    const nf = [...flipped, i]; setFlipped(nf);
    const nt = tries - 1; setTries(nt);
    if (cards[i].win) { setWon(cards[i].text); setDone(true); setTimeout(() => setFlipped(cards.map((_, j) => j)), 800); }
    else if (nt <= 0) { setDone(true); setTimeout(() => setFlipped(cards.map((_, j) => j)), 600); }
  };

  const reset = () => { setCards([...PRIZES].sort(() => Math.random() - 0.5)); setFlipped([]); setTries(2); setDone(false); setWon(null); };

  return (
    <div className="flex flex-col items-center pt-2" data-testid="mini-instant">
      <p className="text-xs font-bold text-[#1D3557] mb-1">Choisissez une carte !</p>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] font-semibold text-white bg-[#C65D47] px-3 py-1 rounded-full">
          {tries} essai{tries > 1 ? "s" : ""} restant{tries > 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 w-[230px]">
        {cards.map((card, i) => {
          const isFlipped = flipped.includes(i);
          return (
            <button key={i} onClick={() => flip(i)} disabled={done || isFlipped} className="aspect-[3/4] rounded-lg overflow-hidden transition-all hover:scale-105 disabled:hover:scale-100">
              <div style={{ transformStyle: "preserve-3d", transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)", transition: "transform 0.6s ease" }} className="relative w-full h-full">
                <div style={{ backfaceVisibility: "hidden" }} className="absolute inset-0 bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] rounded-lg flex flex-col items-center justify-center border-2 border-[#8C9D78]/40 shadow-md">
                  <span className="text-lg">?</span>
                  <Gift className="w-5 h-5 text-[#FFD700] mb-0.5" />
                  <span className="text-[8px] text-white/80 font-bold">OUVRIR</span>
                </div>
                <div style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }} className={`absolute inset-0 rounded-lg flex flex-col items-center justify-center ${card.win ? "bg-gradient-to-br from-[#8C9D78] to-[#6B7C5A] border-2 border-[#FFD700]" : "bg-stone-200"}`}>
                  {card.win ? <><Sparkles className="w-5 h-5 text-[#FFD700] mb-0.5" /><span className="text-xs font-bold text-white">{card.text}</span></> : <span className="text-xs font-bold text-stone-400">PERDU</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {done && (
        <div className={`text-center px-4 py-2 rounded-xl mb-2 text-xs font-bold ${won ? "bg-[#8C9D78]/20 text-[#1D3557]" : "bg-stone-100 text-stone-500"}`}>
          {won ? `Bravo ! Vous avez gagné : ${won}` : "Pas de chance cette fois !"}
        </div>
      )}
      <button onClick={reset} className="text-xs px-5 py-2 rounded-full text-white font-semibold bg-[#C65D47] hover:bg-[#A84A38]">Rejouer</button>
    </div>
  );
};

/* ==============================
   MINI SLOT MACHINE
   ============================== */
const MiniSlots = () => {
  const SYMS = ["🍒", "🍋", "🍇", "7️⃣", "💎", "⭐"];
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels] = useState(["🍒", "🍇", "💎"]);
  const [result, setResult] = useState(null);
  const [credits, setCredits] = useState(10);

  const spin = () => {
    if (spinning || credits <= 0) return;
    setSpinning(true); setResult(null); setCredits(c => c - 1);
    const win = Math.random() > 0.6;
    let anim = 0;
    const interval = setInterval(() => {
      setReels([SYMS[Math.floor(Math.random() * 6)], SYMS[Math.floor(Math.random() * 6)], SYMS[Math.floor(Math.random() * 6)]]);
      anim++;
      if (anim > 15) {
        clearInterval(interval);
        let final;
        if (win) { const s = SYMS[Math.floor(Math.random() * 6)]; final = [s, s, s]; }
        else { final = [SYMS[Math.floor(Math.random() * 6)], SYMS[Math.floor(Math.random() * 6)], SYMS[Math.floor(Math.random() * 6)]]; }
        setReels(final);
        setSpinning(false);
        if (final[0] === final[1] && final[1] === final[2]) {
          setResult("win"); setCredits(c => c + 5);
        } else { setResult("lose"); }
      }
    }, 80);
  };

  return (
    <div className="flex flex-col items-center pt-2" data-testid="mini-slots">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-bold text-[#FFD700] bg-[#FFD700]/15 px-2.5 py-1 rounded-full">{credits} crédits</span>
      </div>

      {/* Machine */}
      <div className="bg-gradient-to-b from-[#1D3557] to-[#152A45] rounded-2xl p-4 shadow-xl w-[220px] mb-3">
        <div className="flex justify-center gap-1.5 mb-2">
          {[0, 1, 2, 3, 4].map(i => <div key={i} className="w-2 h-2 rounded-full transition-all" style={{ backgroundColor: spinning ? ["#C65D47", "#FFD700", "#8C9D78", "#4FC3F7", "#C65D47"][i] : "#334155", boxShadow: spinning ? `0 0 6px ${["#C65D47", "#FFD700", "#8C9D78", "#4FC3F7", "#C65D47"][i]}` : "none" }} />)}
        </div>
        <p className="text-[#FFD700] text-[10px] font-bold tracking-widest text-center mb-2">MOREATI SLOTS</p>
        <div className="bg-stone-100 rounded-xl p-3 flex items-center justify-center gap-2 relative">
          <div className="absolute left-2 right-2 h-[1px] bg-[#C65D47]/30 z-10" />
          {reels.map((s, i) => (
            <div key={i} className={`w-14 h-14 bg-white rounded-lg flex items-center justify-center border border-stone-200 ${spinning ? "animate-pulse" : ""}`}>
              <span className="text-2xl select-none">{s}</span>
            </div>
          ))}
        </div>
      </div>

      {result && (
        <div className={`text-center px-4 py-1.5 rounded-lg mb-2 text-[10px] font-bold ${result === "win" ? "bg-[#FFD700]/20 text-[#1D3557]" : "bg-stone-100 text-stone-400"}`}>
          {result === "win" ? "JACKPOT ! +5 crédits" : "Pas de chance !"}
        </div>
      )}

      <button onClick={spin} disabled={spinning || credits <= 0} className="flex items-center gap-1.5 px-5 py-2 rounded-full text-white text-xs font-semibold disabled:opacity-50 transition-all" style={{ backgroundColor: "#C65D47" }}>
        {spinning ? "..." : credits <= 0 ? "Plus de crédits" : `SPIN (${credits})`}
      </button>
    </div>
  );
};

/* ==============================
   MINI LOTTERY DRAW
   ============================== */
const MiniLottery = () => {
  const [picked, setPicked] = useState([]);
  const [drawn, setDrawn] = useState([]);
  const [phase, setPhase] = useState("pick");
  const [matches, setMatches] = useState(0);
  const [currentBall, setCurrentBall] = useState(null);
  const nums = Array.from({ length: 20 }, (_, i) => i + 1);

  const toggle = (n) => {
    if (phase !== "pick") return;
    if (picked.includes(n)) setPicked(picked.filter(x => x !== n));
    else if (picked.length < 4) setPicked([...picked, n]);
  };

  const quickPick = () => {
    if (phase !== "pick") return;
    const shuffled = [...nums].sort(() => Math.random() - 0.5);
    setPicked(shuffled.slice(0, 4).sort((a, b) => a - b));
  };

  const draw = () => {
    if (picked.length !== 4) return;
    setPhase("drawing"); setDrawn([]); setMatches(0);
    const pool = [...nums]; const winning = [];
    for (let i = 0; i < 4; i++) { const idx = Math.floor(Math.random() * pool.length); winning.push(pool[idx]); pool.splice(idx, 1); }
    let d = [], m = 0;
    winning.forEach((n, i) => {
      setTimeout(() => {
        setCurrentBall(n);
        setTimeout(() => {
          d = [...d, n]; setDrawn([...d]); setCurrentBall(null);
          if (picked.includes(n)) { m++; setMatches(m); }
          if (i === 3) setTimeout(() => setPhase("result"), 500);
        }, 600);
      }, i * 1200);
    });
  };

  const reset = () => { setPicked([]); setDrawn([]); setPhase("pick"); setMatches(0); setCurrentBall(null); };

  return (
    <div className="flex flex-col items-center pt-1" data-testid="mini-lottery">
      {phase === "pick" && (
        <>
          <p className="text-[10px] font-semibold text-[#1D3557] mb-2">Choisissez 4 numéros</p>
          <div className="grid grid-cols-5 gap-1 mb-2 w-[200px]">
            {nums.map(n => (
              <button key={n} onClick={() => toggle(n)} className={`w-8 h-8 rounded-full text-[10px] font-bold transition-all ${picked.includes(n) ? "bg-[#C65D47] text-white scale-110 shadow-md" : "bg-stone-100 text-stone-500 hover:bg-[#8C9D78]/20"}`}>{n}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 mb-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${picked[i] ? "bg-[#1D3557] text-white border-[#1D3557]" : "border-dashed border-stone-300 text-stone-300"}`}>
                {picked[i] || "?"}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={quickPick} className="text-[10px] px-3 py-1.5 rounded-full border border-[#8C9D78]/30 text-[#8C9D78] font-medium">Auto</button>
            <button onClick={draw} disabled={picked.length !== 4} className="text-[10px] px-4 py-1.5 rounded-full text-white font-semibold disabled:opacity-40" style={{ backgroundColor: "#C65D47" }}>
              Lancer le tirage
            </button>
          </div>
        </>
      )}

      {(phase === "drawing" || phase === "result") && (
        <>
          <p className="text-[9px] text-stone-400 uppercase tracking-wider mb-1">Vos numéros</p>
          <div className="flex gap-1 mb-3">
            {picked.sort((a, b) => a - b).map(n => (
              <div key={n} className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${drawn.includes(n) ? "bg-[#8C9D78] text-white scale-110 ring-2 ring-[#8C9D78]/50" : "bg-[#1D3557] text-white"}`}>{n}</div>
            ))}
          </div>
          {currentBall && (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA000] flex items-center justify-center shadow-xl animate-bounce mb-2">
              <span className="text-[#1D3557] font-bold text-sm">{currentBall}</span>
            </div>
          )}
          <p className="text-[9px] text-stone-400 uppercase tracking-wider mb-1">Numéros tirés</p>
          <div className="flex gap-1 mb-2">
            {[0, 1, 2, 3].map(i => {
              const n = drawn[i]; const match = n && picked.includes(n);
              return <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${n ? (match ? "bg-[#8C9D78] text-white" : "bg-[#C65D47] text-white") : "bg-stone-200"}`}>{n || ""}</div>;
            })}
          </div>
          <p className="text-[10px] font-bold text-[#1D3557] mb-2">{matches} correspondance{matches > 1 ? "s" : ""}</p>
          {phase === "result" && (
            <>
              <div className={`px-4 py-1.5 rounded-lg mb-2 text-[10px] font-bold ${matches >= 3 ? "bg-[#FFD700]/20 text-[#1D3557]" : matches >= 1 ? "bg-[#8C9D78]/20 text-[#1D3557]" : "bg-stone-100 text-stone-400"}`}>
                {matches >= 4 ? "JACKPOT !" : matches >= 3 ? "SUPER GAIN !" : matches >= 2 ? "Petit gain" : matches >= 1 ? "Remboursé" : "Perdu"}
              </div>
              <button onClick={reset} className="text-[10px] px-4 py-1.5 rounded-full text-white font-semibold" style={{ backgroundColor: "#C65D47" }}>Rejouer</button>
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ==============================
   EXPORT MAP
   ============================== */
const PhoneGames = { MiniWheel, MiniScratch, MiniInstant, MiniSlots, MiniLottery };
export default PhoneGames;
